import React, { useState, useEffect } from "react";
import "./../styles/gameboarding.scss";

const characters = {
  steven: "images/avatar3-Dk8Y3DRT.svg",
  offmylawn: "images/avatar4-Bb8RIjZv.svg",
};

const GameBoard = ({ playerCharacter, playerName, onQuit }) => {
  // Board state
  const [board, setBoard] = useState(Array(9).fill(null));
  const [isPlayerTurn, setIsPlayerTurn] = useState(true);

  // Scores
  const [playerScore, setPlayerScore] = useState(0);
  const [computerScore, setComputerScore] = useState(0);

  // For tracking draws
  const [draws, setDraws] = useState(0);

  // For overlay / winner screen
  const [winner, setWinner] = useState(null);

  // Computer’s character is the opposite of the player’s
  const computerCharacter =
    playerCharacter === "steven" ? "offmylawn" : "steven";

  // Decide the name for the computer
  const computerName =
    computerCharacter === "steven" ? "STEVEN THE OWL" : "OFFMYLAWN";

  // For turn display
  const currentTurnName = isPlayerTurn ? playerName : computerName;

  // All possible winning combos
  const winningCombinations = [
    [0, 1, 2],
    [3, 4, 5],
    [6, 7, 8],
    [0, 3, 6],
    [1, 4, 7],
    [2, 5, 8],
    [0, 4, 8],
    [2, 4, 6],
  ];

  // Check for winner or draw
  const checkWinner = (squares) => {
    for (let [a, b, c] of winningCombinations) {
      if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
        return squares[a];
      }
    }
    return squares.includes(null) ? null : "draw";
  };

  // --- MINIMAX LOGIC --- //
  const minimax = (newBoard, depth, isMaximizing) => {
    const result = checkWinner(newBoard);
    if (result === playerCharacter) return -10 + depth; // Player wins -> negative
    if (result === computerCharacter) return 10 - depth; // Computer wins -> positive
    if (result === "draw") return 0;

    // Introduce a small chance of making a random move (10%)
    const randomFactor = Math.random();
    if (randomFactor < 0.1) {
      // 10% chance to just pick a random move
      return isMaximizing ? -5 : 5;
    }

    if (isMaximizing) {
      let bestScore = -Infinity;
      for (let i = 0; i < 9; i++) {
        if (!newBoard[i]) {
          newBoard[i] = computerCharacter;
          let score = minimax(newBoard, depth + 1, false);
          newBoard[i] = null;
          bestScore = Math.max(score, bestScore);
        }
      }
      return bestScore;
    } else {
      let bestScore = Infinity;
      for (let i = 0; i < 9; i++) {
        if (!newBoard[i]) {
          newBoard[i] = playerCharacter;
          let score = minimax(newBoard, depth + 1, true);
          newBoard[i] = null;
          bestScore = Math.min(score, bestScore);
        }
      }
      return bestScore;
    }
  };

  // Computer picks the best move using Minimax
  const computerMove = () => {
    let bestScore = -Infinity;
    let move;
    for (let i = 0; i < 9; i++) {
      if (!board[i]) {
        let newBoard = [...board];
        newBoard[i] = computerCharacter;
        let score = minimax(newBoard, 0, false);
        newBoard[i] = null;
        if (score > bestScore) {
          bestScore = score;
          move = i;
        }
      }
    }
    if (move !== undefined) {
      const newBoard = [...board];
      newBoard[move] = computerCharacter;
      setBoard(newBoard);
      setIsPlayerTurn(true);
    }
  };
  // ---------------------- //

  // Handle player click
  const handleCellClick = (index) => {
    // If cell is occupied or game over or it's not player’s turn
    if (board[index] || winner || !isPlayerTurn) return;

    let newBoard = [...board];
    newBoard[index] = playerCharacter;
    setBoard(newBoard);
    setIsPlayerTurn(false);
  };

  // Watch board changes
  useEffect(() => {
    // Check if there's a winner or draw
    const result = checkWinner(board);
    if (result) {
      // If it's a draw
      if (result === "draw") {
        setDraws(draws + 1);
        setWinner("draw");
      } else {
        // If there's a winner, update scores
        if (result === playerCharacter) {
          setPlayerScore(playerScore + 1);
          setWinner(playerName); // show player's name
        } else {
          setComputerScore(computerScore + 1);
          setWinner(computerName); // show computer's name
        }
      }
      return; // Stop here
    }

    // If no winner and it's computer's turn, let the AI move
    if (!isPlayerTurn) {
      const timer = setTimeout(() => {
        computerMove();
      }, 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line
  }, [board, isPlayerTurn]);

  // Reset the board, keep scores
  // "winnerName" is either the player's name, the computer's name, or "draw"
  const resetGame = (winnerName) => {
    setBoard(Array(9).fill(null));
    setWinner(null);

    // If the player was the winner, let the AI move first
    // => setIsPlayerTurn(false) means the computer will move next
    if (winnerName === playerName) {
      setIsPlayerTurn(false);
    } else {
      setIsPlayerTurn(true);
    }
  };

  // If we have a winner or draw, show overlay
  const showWinnerOverlay = winner !== null;

  return (
    <div className="game-board-container">
      {/* Scoreboard */}
      <div className="scoreboard">
        <div className="player-score">
          <img src={characters[playerCharacter]} alt="Player Icon" />
          <div className="score-details">
            <h3>{playerName}</h3>
            <p>{playerScore}</p>
          </div>
        </div>
        <span>VS</span>
        <div className="player-score">
          <img src={characters[computerCharacter]} alt="Computer Icon" />
          <div className="score-details">
            <h3>{computerName}</h3>
            <p>{computerScore}</p>
          </div>
        </div>
      </div>

      {/* Turn Indicator */}
      {!showWinnerOverlay && (
        <h2 className="turn-indicator">
          THE ULTIMATE TIC-TAC-TOE  
          <br />
          <span>{currentTurnName} turn to play</span>
        </h2>
      )}

      {/* LINE-BASED BOARD */}
      <div className="board">
        {/* 4 lines to create the grid */}
        <div className="line h1"></div>
        <div className="line h2"></div>
        <div className="line v1"></div>
        <div className="line v2"></div>

        {/* 9 cells, absolutely positioned */}
        {board.map((cell, index) => (
          <div
            key={index}
            className={`cell cell-${index}`}
            onClick={() => handleCellClick(index)}
          >
            {cell && <img src={characters[cell]} alt={cell} />}
          </div>
        ))}
      </div>

      {/* Winner / Draw Overlay */}
      {showWinnerOverlay && (
        <div className="winner-overlay">
          <div className="winner-content">
            {winner === "draw" ? (
              <>
                <h1>IT'S A DRAW!</h1>
              </>
            ) : (
              <>
                {/* Show the winner’s character image */}
                <img
                  src={
                    winner === playerName
                      ? characters[playerCharacter]
                      : characters[computerCharacter]
                  }
                  alt="winner"
                  className="winner-img"
                />
                <h1>WINNER!</h1>
              </>
            )}

            <div className="overlay-buttons">
              <button
                onClick={() => resetGame(winner)}
                style={{ backgroundColor: "yellow", color: "black" }}
              >
                Play Again
              </button>
              <button
                onClick={onQuit}
                style={{ backgroundColor: "red", color: "white" }}
              >
                Quit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Draw Counter */}
      <p className="draws-count">Draws: {draws}</p>
    </div>
  );
};

export default GameBoard;